import React from 'react';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { MagicWand, User, UserCircle } from '@phosphor-icons/react';

const MatchBadge = ({ match }) => {
  if (!match?.confidence) return null;
  const isLearned = match.source === 'learned_correction';
  return (
    <Badge
      className={`ml-1 text-[10px] h-4 px-1.5 ${
        isLearned
          ? 'bg-indigo-100 text-indigo-700'
          : 'bg-emerald-100 text-emerald-700'
      }`}
      title={
        isLearned
          ? 'Auto-applied from previous correction'
          : 'AI fuzzy match'
      }
    >
      {isLearned ? '🧠 Learned' : `AI ${match.confidence}%`}
    </Badge>
  );
};

const MatchingRow = ({
  tsId,
  editable,
  patientId,
  setPatientId,
  employeeId,
  setEmployeeId,
  patients,
  employees,
  ts,
  displayPatientName,
  displayEmployeeName,
}) => (
  <div className="px-5 py-4 grid grid-cols-1 md:grid-cols-2 gap-4 border-b border-slate-100 bg-slate-50/50">
    {/* Patient */}
    <div>
      <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1 mb-1">
        <User size={12} /> Patient
        <MatchBadge match={ts.patient_match} />
      </label>
      {editable ? (
        <Select value={patientId} onValueChange={setPatientId}>
          <SelectTrigger
            className="h-9 text-sm bg-white"
            data-testid={`patient-select-${tsId}`}
          >
            <SelectValue placeholder="Select patient…" />
          </SelectTrigger>
          <SelectContent>
            {patients.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.first_name} {p.last_name}
                {p.medicaid_id_last4 ? ` (•••${p.medicaid_id_last4})` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <p className="text-sm font-medium py-1.5">{displayPatientName}</p>
      )}
      {ts.extracted_data?.patient_name && editable && (
        <p className="text-[11px] text-slate-400 mt-1 flex items-center gap-1">
          <MagicWand size={10} /> AI read:{' '}
          <span className="italic">{ts.extracted_data.patient_name}</span>
        </p>
      )}
    </div>
    {/* Employee */}
    <div>
      <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1 mb-1">
        <UserCircle size={12} /> Employee / Aide
        <MatchBadge match={ts.employee_match} />
      </label>
      {editable ? (
        <Select value={employeeId} onValueChange={setEmployeeId}>
          <SelectTrigger
            className="h-9 text-sm bg-white"
            data-testid={`employee-select-${tsId}`}
          >
            <SelectValue placeholder="Select employee…" />
          </SelectTrigger>
          <SelectContent>
            {employees.map((e) => (
              <SelectItem key={e.id} value={e.id}>
                {e.first_name} {e.last_name}
                {e.employee_id ? ` (${e.employee_id})` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <p className="text-sm font-medium py-1.5">{displayEmployeeName || '—'}</p>
      )}
      {ts.extracted_data?.staff_name && editable && (
        <p className="text-[11px] text-slate-400 mt-1 flex items-center gap-1">
          <MagicWand size={10} /> AI read:{' '}
          <span className="italic">{ts.extracted_data.staff_name}</span>
        </p>
      )}
    </div>
  </div>
);

export default MatchingRow;
